import { marker as _ } from '@vandres/ngx-translate-extract-marker';

export type ChangeKind = 'new' | 'improved' | 'fixed';

export interface ChangelogEntry {
  kind: ChangeKind;
  /** English source string; also the i18n key. */
  title: string;
  description: string;
}

export interface ChangelogDay {
  /** Release date as `YYYY-MM-DD`. */
  date: string;
  entries: readonly ChangelogEntry[];
}

export const CHANGE_KIND_LABELS: Record<ChangeKind, string> = {
  new: _('New'),
  improved: _('Improved'),
  fixed: _('Fixed'),
};

/**
 * User-facing changes shown in the About page's Changelog tab, newest first.
 * Describe what users notice, not how it is built. Wrap every string in `_()`,
 * add it to `en.json` and fill in the other languages (see `translate.py`).
 */
export const CHANGELOG: readonly ChangelogDay[] = [
  {
    date: '2026-10-16',
    entries: [
      {
        kind: 'new',
        title: _('Show any tile as a presentation'),
        description: _(
          "Put any participant on stage just for yourself, from the tile's buttons or with a double tap. It stays there until someone starts a new screen share."
        ),
      },
      {
        kind: 'improved',
        title: _('Screen sharing presents right away'),
        description: _(
          'The share button starts and stops your presentation directly, and the newest screen share always takes the stage.'
        ),
      },
      {
        kind: 'improved',
        title: _('Steadier connection graphs'),
        description: _(
          'Ping and connection graphs no longer turn small fluctuations into big swings.'
        ),
      },
    ],
  },
  {
    date: '2026-10-15',
    entries: [
      {
        kind: 'new',
        title: _('Stream health at a glance'),
        description: _(
          'Video tiles warn when a stream plays badly or shows no picture. The badge opens detailed stream stats with a hint where the problem likely is.'
        ),
      },
      {
        kind: 'new',
        title: _('Detailed connection panel'),
        description: _(
          'The connection panel shows overall quality, ping, packet loss and throughput with their recent history, plus the health of every visible video.'
        ),
      },
      {
        kind: 'new',
        title: _('Favorites'),
        description: _(
          'Click a participant in the user list to mark them as a favorite. Favorites are highlighted and get a star on their video tile.'
        ),
      },
      {
        kind: 'improved',
        title: _('Redesigned video tiles'),
        description: _(
          'Tiles keep a 16:9 shape and make the most of the available space. Name, mute status and raised hands float on the picture, and a raised hand moves the tile forward.'
        ),
      },
      {
        kind: 'improved',
        title: _('A calmer look'),
        description: _(
          'Solid, quieter surfaces replace the frosted glass, with clearer contrast in light mode and the user list shown as tiles.'
        ),
      },
      {
        kind: 'fixed',
        title: _('Tooltips on touch screens'),
        description: _(
          'Tooltips no longer stick after a tap. Touch and hold an element to see its tooltip.'
        ),
      },
    ],
  },
  {
    date: '2026-10-14',
    entries: [
      {
        kind: 'new',
        title: _('Share files in rooms'),
        description: _(
          'Share files from the paperclip menu or by drag and drop. Files can be encrypted with a password in your browser and kept for an hour, a day, a week or as long as the room exists.'
        ),
      },
      {
        kind: 'improved',
        title: _('Better protected transcripts and recordings'),
        description: _(
          'Only members admitted to a room can read its transcript or start and stop a recording.'
        ),
      },
    ],
  },
  {
    date: '2026-10-13',
    entries: [
      {
        kind: 'new',
        title: _('Trial countdown'),
        description: _(
          'During a trial, the remaining time is shown before you join and as a notification in the meeting.'
        ),
      },
    ],
  },
  {
    date: '2026-10-12',
    entries: [
      {
        kind: 'new',
        title: _('Join any room'),
        description: _(
          'A new role privilege lets members enter private rooms and rooms outside their scheduled time without knocking. Instant meetings can now only be started by room managers.'
        ),
      },
      {
        kind: 'improved',
        title: _('Previous addresses redirect'),
        description: _(
          'When the app moves to a new address, the previous one forwards there for 30 days.'
        ),
      },
    ],
  },
  {
    date: '2026-10-08',
    entries: [
      {
        kind: 'new',
        title: _('Navigation bar and meeting clock'),
        description: _(
          'Panel actions moved into a navigation bar above the main view. The room header shows how long you have been connected, the scheduled time and whether the meeting runs over.'
        ),
      },
    ],
  },
];
